//封装定义Request类
import axios, { AxiosRequestConfig, AxiosResponse, AxiosInstance } from 'axios'
import { ElLoading, ElMessage } from 'element-plus'
type ILoadingInstance = ReturnType<typeof ElLoading.service>
import { R } from '@/api/common-types'

interface LcRequestConfig<T = AxiosResponse> extends AxiosRequestConfig {
  interceptors?: LcRequestInterceptors<T>
  showLoading?: boolean
  showPrompt?: boolean
  showPromptErrorMessage?: string
  showPromptSuccessMessage?: string
}

interface LcRequestInterceptors<T = AxiosResponse> {
  requestInterceptor?: (config: AxiosRequestConfig) => AxiosRequestConfig
  requestInterceptorCatch?: (error: any) => any
  responseInterceptor?: (res: T) => T
  responseInterceptorCatch?: (error: any) => any
}

//响应结果弹出提示
const DEFAULT_SHOW_PROMPT_ERROR_MESSAGE = '请求失败'
const DEFAULT_SHOW_PROMPT_SUCCESS_MESSAGE = '请求成功'

const DEFAULT_LOADING = true
const DEFAULT_SHOW_ERROR = false

class LcRequest {
  instance: AxiosInstance
  interceptors?: LcRequestInterceptors
  showLoading: boolean
  showPrompt: boolean
  showPromptErrorMessage?: string
  showPromptSuccessMessage?: string
  loading?: ILoadingInstance

  constructor(config: LcRequestConfig) {
    this.instance = axios.create(config)
    this.showLoading = config.showLoading ?? DEFAULT_LOADING
    this.showPrompt = config.showPrompt ?? DEFAULT_SHOW_ERROR
    this.showPromptErrorMessage = config.showPromptErrorMessage ?? DEFAULT_SHOW_PROMPT_ERROR_MESSAGE
    this.showPromptSuccessMessage = config.showPromptSuccessMessage ?? DEFAULT_SHOW_PROMPT_SUCCESS_MESSAGE
    this.interceptors = config.interceptors

    if (this.interceptors?.requestInterceptor || this.interceptors?.requestInterceptorCatch) {
      this.instance.interceptors.request.use(this.interceptors.requestInterceptor, this.interceptors.requestInterceptorCatch)
    }

    if (this.interceptors?.responseInterceptor || this.interceptors?.responseInterceptorCatch) {
      this.instance.interceptors.response.use(this.interceptors.responseInterceptor, this.interceptors.responseInterceptorCatch)
    }

    this.instance.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        if (this.showLoading) {
          this.loading = ElLoading.service({
            lock: true,
            text: '正在请求数据....',
            background: 'rgba(0, 0, 0, 0.5)'
          })
        }
        return config
      },
      (error: any) => {
        if (this.loading) this.loading.close()
        return Promise.reject(error)
      }
    )

    this.instance.interceptors.response.use(
      <T>(response: AxiosResponse<R<T>>) => {
        const responseData = response.data

        // 业务失败（success 为 false）：始终提示后端 message，并 reject 给调用方
        if (responseData && typeof responseData.success === 'boolean' && !responseData.success) {
          if (this.loading) this.loading.close()

          const errorMessage = responseData.message || this.showPromptErrorMessage || DEFAULT_SHOW_PROMPT_ERROR_MESSAGE
          ElMessage.error(errorMessage)

          const err: any = new Error(errorMessage)
          err.response = response
          err.responseData = responseData
          err._messageShown = true
          return Promise.reject(err)
        }

        if (this.loading) this.loading.close()
        // 仅显式开启 showPrompt 时提示成功（避免查询类接口误弹「请求成功」）
        if (this.showPrompt) {
          ElMessage.success(this.showPromptSuccessMessage || responseData?.message || DEFAULT_SHOW_PROMPT_SUCCESS_MESSAGE)
        }

        // 返回原始的response对象，但使用我们的解析过的data
        return { ...response, data: responseData }
      },
      (error: any) => {
        if (this.loading) this.loading.close()

        // 已提示过业务错误则不再重复弹窗
        if (error?._messageShown) {
          return Promise.reject(error)
        }

        let errorMessage = this.showPromptErrorMessage || DEFAULT_SHOW_PROMPT_ERROR_MESSAGE
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }
        ElMessage.error(errorMessage)
        error._messageShown = true
        return Promise.reject(error)
      }
    )
  }

  // LcRequest 类的 request 方法中添加对 showPrompt 的处理
  request<T>(config: LcRequestConfig<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      // 保存当前实例状态以便后续恢复
      const originalShowPrompt = this.showPrompt
      const originalShowPromptSuccessMessage = this.showPromptSuccessMessage
      const originalShowPromptErrorMessage = this.showPromptErrorMessage
      const originalShowLoading = this.showLoading

      // 如果请求配置中包含 showPrompt，则临时覆盖实例的 showPrompt 设置
      if (typeof config.showPrompt !== 'undefined') {
        this.showPrompt = config.showPrompt
      }
      if (config.showPromptSuccessMessage) {
        this.showPromptSuccessMessage = config.showPromptSuccessMessage
      }
      if (config.showPromptErrorMessage) {
        this.showPromptErrorMessage = config.showPromptErrorMessage
      }
      // 判断是否需要显示loading
      if (typeof config.showLoading !== 'undefined') {
        this.showLoading = config.showLoading
      }

      // 单个请求对请求config的处理
      if (config.interceptors?.requestInterceptor) {
        config = config.interceptors.requestInterceptor(config)
      }

      const restore = () => {
        this.showPrompt = originalShowPrompt
        this.showPromptSuccessMessage = originalShowPromptSuccessMessage
        this.showPromptErrorMessage = originalShowPromptErrorMessage
        this.showLoading = originalShowLoading
      }

      this.instance
        .request<any, T>(config)
        .then((res) => {
          // 单个请求对数据的处理
          if (config.interceptors?.responseInterceptor) {
            res = config.interceptors.responseInterceptor(res)
          }

          restore()
          // 检查返回的数据是否符合 R<T> 格式，如果是则提取 data，否则直接返回原数据
          if (res && typeof res === 'object' && 'data' in res) {
            resolve((res as any).data || res)
          } else {
            resolve(res)
          }
        })
        .catch((err) => {
          restore()
          reject(err)
        })
    })
  }

  get<T>(config: LcRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'GET' })
  }

  post<T>(config: LcRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'POST' })
  }

  delete<T>(config: LcRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE' })
  }

  patch<T>(config: LcRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'PATCH' })
  }
}

export default LcRequest
